/**
 * A Foundry member of a team the organisation holds (workbench D273 round C): no machine keeps such a team,
 * so no workbench publishes its Foundry agents; an administrator asks a node to, here. One line per member
 * (status, then at most two actions), the last order's refusal under it, a small dialog that picks the node,
 * and a confirmation before the agent is deleted. Unlinking an adopted agent is the workbench's: the team
 * routes have no unlink, so an adopted agent shows no action at all.
 */

import { Modal } from '@heroui/react';
import { useState } from 'react';

import { problemText, routes, type TeamCloudHolder } from '../api/client.ts';
import { useLoad } from '../hooks.ts';
import { Button, ChoiceSelect, ErrorNote, StatusMark } from './ui.tsx';

type Suggestion = import('@aioe/shared').FoundrySuggestion;

/** The member's state in words, with the mark that goes with it. */
export function foundryStateWords(h: TeamCloudHolder, noNode: boolean): { state: 'online' | 'idle' | 'offline' | 'working'; label: string } {
  const f = h.foundry;
  if (f?.lastOrder?.state === 'pending') return { state: 'working', label: f.lastOrder.kind === 'foundry.publish' ? 'Publishing' : 'Deleting' };
  if (f?.published && f.outOfDate) return { state: 'idle', label: 'Out of date since the member changed' };
  if (f?.published) return { state: 'online', label: `Published as version ${h.agentVersion ?? '?'} in project ${h.projectHost}` };
  if (noNode) return { state: 'offline', label: 'Foundry member not yet published: no node holds a project this team is granted to' };
  return { state: 'offline', label: 'Not published yet' };
}

/** What the last order's answer says, in a sentence; nothing when it went well. */
export function foundryOrderNote(h: TeamCloudHolder): string | undefined {
  const o = h.foundry?.lastOrder;
  if (!o) return undefined;
  const what = o.kind === 'foundry.publish' ? 'publish this agent' : 'delete this agent';
  if (o.state === 'pending') return `Asked ${o.deviceName} to ${what}; waiting for it to answer.`;
  if (o.state === 'refused' || o.state === 'failed') return `${o.deviceName} ${o.state === 'refused' ? 'refused to' : 'could not'} ${what}${o.message ? `: ${o.message.replace(/\.$/, '')}` : ''}.`;
  return undefined;
}

export function FoundryPublishLine({ teamId, h, onChanged }: { teamId: string; h: TeamCloudHolder; onChanged: () => void }) {
  const [publishing, setPublishing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Only administrators may ask; the suggestion route says so (a 403 leaves the line as words only).
  const suggestion = useLoad(() => routes.foundrySuggest(teamId, h.member).catch(() => null), [teamId, h.member, h.foundry?.lastOrder?.state]);
  const s = suggestion.data;
  const mayAsk = !!s;
  const noNode = !!s && !s.node;
  const words = foundryStateWords(h, noNode);
  const note = foundryOrderNote(h);
  const pending = h.foundry?.lastOrder?.state === 'pending';
  const published = !!h.foundry?.published;
  const offerPublish = mayAsk && !!s?.node && !pending && (!published || h.foundry?.outOfDate);
  const offerDelete = mayAsk && published && !h.adopted && !pending;
  return (
    <li className="grid gap-1 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <span className="min-w-0">
          <span className="font-medium">{h.member}</span> <span className="text-muted">{h.words}</span>
        </span>
        <span className="inline-flex flex-wrap items-center gap-x-2">
          {offerPublish ? (
            <Button size="sm" variant="secondary" onPress={() => setPublishing(true)}>
              Publish to Foundry from {s!.node!.name}
            </Button>
          ) : null}
          {offerDelete ? (
            <Button size="sm" variant="ghost" onPress={() => setDeleting(true)}>
              Delete the Foundry agent
            </Button>
          ) : null}
        </span>
      </div>
      <StatusMark state={words.state} label={words.label} />
      {h.adopted && published ? <p className="m-0 text-xs text-muted">Built in the Foundry portal: it is run as it is, never rewritten or deleted from here.</p> : null}
      {note ? <p className="m-0 text-xs text-muted">{note}</p> : null}
      {publishing && s ? <PublishDialog teamId={teamId} h={h} s={s} onClose={() => setPublishing(false)} onIssued={onChanged} /> : null}
      {deleting && s ? <DeleteDialog teamId={teamId} h={h} s={s} onClose={() => setDeleting(false)} onIssued={onChanged} /> : null}
    </li>
  );
}

/** The node picker, preselected from the suggestion. */
function useNodeChoice(s: Suggestion) {
  const options = Object.fromEntries(s.nodes.map((n) => [n.deviceId, `${n.name}${n.projectName ? ` (${n.projectName})` : ''}${n.fits ? '' : ', project rules refuse this'}`])) as Record<string, string>;
  const [picked, setPicked] = useState<string>();
  const chosen = picked && options[picked] ? picked : s.node?.deviceId;
  return { options, chosen, setPicked, fits: s.nodes.find((n) => n.deviceId === chosen)?.fits ?? true };
}

function Ask({ title, kind, teamId, h, s, onClose, onIssued, body, action, danger }: { title: string; kind: 'publish' | 'unpublish'; teamId: string; h: TeamCloudHolder; s: Suggestion; onClose: () => void; onIssued: () => void; body: string; action: string; danger?: boolean }) {
  const { options, chosen, setPicked, fits } = useNodeChoice(s);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string>();
  async function send() {
    if (!chosen) return;
    setSending(true);
    setError(undefined);
    try {
      await routes.foundryOrder(kind, teamId, { memberId: h.member, deviceId: chosen });
      onIssued();
      onClose();
    } catch (e) {
      setError(problemText(e));
    } finally {
      setSending(false);
    }
  }
  return (
    <Modal isOpen onOpenChange={(open) => !open && onClose()}>
      <Modal.Backdrop>
        <Modal.Container placement="center" size="md">
          <Modal.Dialog aria-label={title}>
            <Modal.Header>
              <Modal.Heading>{title}</Modal.Heading>
            </Modal.Header>
            <Modal.Body className="grid gap-3 p-1">
              <p className="m-0 text-sm text-muted">{body}</p>
              <ChoiceSelect label="Node" value={chosen} options={options} onChange={setPicked} placeholder="Choose a node" />
              {!fits ? <p className="m-0 text-xs text-muted">The rules of this node's project do not allow the Foundry deployment's provider, so it will be refused.</p> : null}
              <ErrorNote error={error} />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="ghost" onPress={onClose}>
                Cancel
              </Button>
              <Button variant={danger ? 'danger-soft' : 'primary'} isPending={sending} isDisabled={!chosen} onPress={send}>
                {action}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}

function PublishDialog(p: { teamId: string; h: TeamCloudHolder; s: Suggestion; onClose: () => void; onIssued: () => void }) {
  return <Ask {...p} kind="publish" title={`Publish ${p.h.member} to Foundry`} action="Publish" body={`The node publishes ${p.h.member} to ${p.h.projectHost} as your organisation's identity. Nothing is deleted by this.`} />;
}

function DeleteDialog(p: { teamId: string; h: TeamCloudHolder; s: Suggestion; onClose: () => void; onIssued: () => void }) {
  return <Ask {...p} kind="unpublish" danger title="Delete the Foundry agent" action="Delete the agent" body={`The node deletes the agent ${p.h.agentName ?? ''} from ${p.h.projectHost} as your organisation's identity. ${p.h.member} stays in the team and can be published again.`} />;
}
